import { ArrowDown, ArrowUp, Minus } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Card, CardContent } from "@/components/ui/card"
import { formatCompactNumber } from "@/lib/format-compact-number"
import { cn } from "@/lib/utils"
import type { ChannelKpis } from "@/types/channel-analytics"

/**
 * OKS-734 — KPI strip for the Channel dashboard. Four tiles (UX Spec §2):
 *
 *   1. Followers now  — current follower count + net Δ over the window.
 *      Direction carried by ▲/▼ glyph + sign (NEVER color alone — a11y).
 *   2. Total views    — Σ roster views in window.
 *   3. Total likes    — Σ roster likes in window.
 *   4. Comments · Shares — Σ roster comments + shares in window.
 *
 * Counts are locale-compacted (K/M/B). Responsive: 2×2 on mobile, 4-up
 * from `lg` (matches the EMV strip).
 */
interface ChannelKpiStripProps {
  kpis: ChannelKpis
}

export function ChannelKpiStrip({ kpis }: ChannelKpiStripProps) {
  const { t } = useTranslation()

  return (
    <section
      className="grid grid-cols-2 gap-4 lg:grid-cols-4"
      data-testid="channel-kpi-strip"
      aria-label={t("channel.kpis.section_aria")}
    >
      <FollowersTile kpis={kpis} />

      <CountTile
        testId="channel-kpi-views"
        label={t("channel.kpis.views.label")}
        value={kpis.total_views}
      />
      <CountTile
        testId="channel-kpi-likes"
        label={t("channel.kpis.likes.label")}
        value={kpis.total_likes}
      />
      <EngagementTile
        comments={kpis.total_comments}
        shares={kpis.total_shares}
      />
    </section>
  )
}

interface CountTileProps {
  testId: string
  label: string
  value: number
}

function CountTile({ testId, label, value }: CountTileProps) {
  const { t } = useTranslation()
  return (
    <Card
      data-testid={testId}
      role="group"
      aria-label={t("channel.kpis.generic_aria", {
        label,
        value: formatCompactNumber(value),
      })}
      className="rounded-xl border p-5"
    >
      <CardContent className="flex flex-col gap-1 p-0">
        <span className="text-muted-foreground text-sm font-medium">
          {label}
        </span>
        <span
          className="text-foreground text-3xl font-semibold tracking-tight"
          aria-hidden
        >
          {formatCompactNumber(value)}
        </span>
      </CardContent>
    </Card>
  )
}

function FollowersTile({ kpis }: { kpis: ChannelKpis }) {
  const { t } = useTranslation()
  const delta = kpis.followers_delta
  const direction = delta > 0 ? "up" : delta < 0 ? "down" : "flat"
  const signed = `${delta > 0 ? "+" : ""}${formatCompactNumber(delta)}`

  const Icon =
    direction === "up" ? ArrowUp : direction === "down" ? ArrowDown : Minus
  // Color is a SECONDARY cue — arrow glyph + sign carry direction (a11y).
  const tone =
    direction === "flat"
      ? "text-muted-foreground"
      : direction === "up"
        ? "text-emerald-600 dark:text-emerald-400"
        : "text-destructive"

  const deltaAria =
    direction === "up"
      ? t("channel.kpis.followers.aria_up", { value: signed })
      : direction === "down"
        ? t("channel.kpis.followers.aria_down", { value: signed })
        : t("channel.kpis.followers.aria_flat")

  return (
    <Card
      data-testid="channel-kpi-followers"
      role="group"
      aria-label={t("channel.kpis.followers.aria", {
        value: formatCompactNumber(kpis.followers_now),
        delta: deltaAria,
      })}
      className="rounded-xl border p-5"
    >
      <CardContent className="flex flex-col gap-1 p-0">
        <span className="text-muted-foreground text-sm font-medium">
          {t("channel.kpis.followers.label")}
        </span>
        <span
          className="text-foreground text-3xl font-semibold tracking-tight"
          aria-hidden
        >
          {formatCompactNumber(kpis.followers_now)}
        </span>
        <span
          className={cn("flex items-center gap-1 text-xs font-medium", tone)}
          aria-hidden
          data-testid="channel-kpi-followers-delta"
        >
          <Icon className="h-3.5 w-3.5 shrink-0" />
          {signed}
        </span>
      </CardContent>
    </Card>
  )
}

function EngagementTile({
  comments,
  shares,
}: {
  comments: number
  shares: number
}) {
  const { t } = useTranslation()
  return (
    <Card
      data-testid="channel-kpi-engagement"
      role="group"
      aria-label={t("channel.kpis.engagement.aria", {
        comments: formatCompactNumber(comments),
        shares: formatCompactNumber(shares),
      })}
      className="rounded-xl border p-5"
    >
      <CardContent className="flex flex-col gap-1 p-0">
        <span className="text-muted-foreground text-sm font-medium">
          {t("channel.kpis.engagement.label")}
        </span>
        <span
          className="text-foreground flex items-baseline gap-2 text-3xl font-semibold tracking-tight"
          aria-hidden
        >
          {formatCompactNumber(comments)}
          <span className="text-muted-foreground text-lg font-normal">
            · {formatCompactNumber(shares)}
          </span>
        </span>
      </CardContent>
    </Card>
  )
}
